/**
 * Maps backend error codes to human-friendly messages.
 * Any code not in this map falls through to sanitiseFallback(),
 * which strips UUIDs and technical jargon from the raw message.
 */

const CODE_MAP: Record<string, string> = {
  // ── Auth ────────────────────────────────────────────────────────────────────
  INVALID_CREDENTIALS:       'Incorrect email / phone or password. Please try again.',
  INVALID_TOKEN:             'Your session has expired. Please log in again.',
  TOKEN_EXPIRED:             'Your session has expired. Please log in again.',
  UNAUTHORIZED:              'You don\'t have permission to perform this action.',
  FORBIDDEN:                 'Access denied. You don\'t have the required permissions.',
  ACCOUNT_DISABLED:          'Your account has been disabled. Contact your administrator.',
  ACCOUNT_SUSPENDED:         'Your account is suspended. Please contact support.',
  TWO_FACTOR_REQUIRED:       'Two-factor authentication is required to continue.',
  INVALID_2FA_CODE:          'Invalid verification code. Please try again.',
  BACKUP_CODE_INVALID:       'Invalid backup code. Please try again.',

  // ── Subscription / Billing ──────────────────────────────────────────────────
  ENTITY_ALREADY_SUBSCRIBED: 'This item is already added to a subscription.',
  ALREADY_SUBSCRIBED:        'This item is already subscribed.',
  SUBSCRIPTION_NOT_FOUND:    'No active subscription found.',
  SUBSCRIPTION_INACTIVE:     'The subscription is not active.',
  SUBSCRIPTION_CANCELLED:    'This subscription has been cancelled.',
  SUBSCRIPTION_PAUSED:       'This subscription is currently paused.',
  PLAN_NOT_FOUND:            'The selected plan could not be found.',
  PLAN_INACTIVE:             'The selected plan is no longer available.',
  INVOICE_NOT_FOUND:         'Invoice not found.',
  INVOICE_ALREADY_PAID:      'This invoice has already been paid.',
  INVOICE_CANCELLED:         'This invoice has been cancelled.',
  PAYMENT_NOT_FOUND:         'Payment record not found.',
  PAYMENT_FAILED:            'Payment could not be processed. Please try again.',
  GATEWAY_NOT_FOUND:         'Payment gateway not found.',
  GATEWAY_INACTIVE:          'The selected payment gateway is inactive.',
  WALLET_INSUFFICIENT:       'Insufficient wallet balance.',
  BALANCE_INSUFFICIENT:      'Insufficient balance to complete this action.',
  DUNNING_RULE_EXISTS:       'A dunning rule with these settings already exists.',
  TEMPLATE_NOT_FOUND:        'PDF template not found.',
  BILLING_PROFILE_REQUIRED:  'Billing profile must be configured before creating billing records. Please set up your billing profile first.',

  // ── Vehicles / Devices ──────────────────────────────────────────────────────
  VEHICLE_NOT_FOUND:         'Vehicle not found.',
  DEVICE_NOT_FOUND:          'Device not found.',
  DEVICE_ALREADY_ASSIGNED:   'This device is already assigned to another vehicle.',
  SIM_NOT_FOUND:             'SIM card not found.',
  SIM_ALREADY_ASSIGNED:      'This SIM is already assigned to another device.',
  DRIVER_NOT_FOUND:          'Driver not found.',
  GEOFENCE_NOT_FOUND:        'Geofence not found.',
  TRIP_NOT_FOUND:            'Trip not found.',

  // ── Users / Permissions ─────────────────────────────────────────────────────
  USER_NOT_FOUND:            'User account not found.',
  USER_ALREADY_EXISTS:       'An account with this email or phone already exists.',
  USER_TYPE_NOT_FOUND:       'User type not found.',
  PERMISSION_DENIED:         'You don\'t have permission to perform this action.',
  CANNOT_DELETE_SYSTEM_TYPE: 'System user types cannot be deleted.',

  // ── General CRUD ────────────────────────────────────────────────────────────
  NOT_FOUND:                 'The requested item could not be found.',
  ENTITY_NOT_FOUND:          'The requested item could not be found.',
  ALREADY_EXISTS:            'A record with these details already exists.',
  DUPLICATE_ENTRY:           'A record with these details already exists.',
  CONFLICT:                  'This action conflicts with existing data. Please refresh and try again.',
  VALIDATION_ERROR:          '', // use raw message for validation — it's usually already user-friendly
  INVALID_INPUT:             'Some fields have invalid values. Please check and try again.',
  MISSING_REQUIRED_FIELD:    'Please fill in all required fields.',

  // ── System / Server ─────────────────────────────────────────────────────────
  RATE_LIMITED:              'Too many requests. Please wait a moment and try again.',
  SERVICE_UNAVAILABLE:       'Service temporarily unavailable. Please try again later.',
  DB_ERROR:                  'A server error occurred. Our team has been notified. Please try again later.',
  INTERNAL_ERROR:            'An unexpected server error occurred. Please try again later.',
  NETWORK_ERROR:             'Cannot reach the server. Please check your connection.',

  // ── Domain / Tenant ─────────────────────────────────────────────────────────
  DOMAIN_NOT_FOUND:          'Domain not found.',
  DOMAIN_INACTIVE:           'This domain is currently inactive.',
  DOMAIN_SUSPENDED:          'This domain has been suspended.',
}

/** UUID pattern — strips UUIDs and surrounding noise from messages */
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi

/**
 * Sanitises a raw backend message as a last-resort fallback:
 * - Strips UUIDs
 * - Removes "error:" / "exception:" prefixes
 * - Capitalises first letter
 * - Returns a generic message if nothing useful remains
 */
function sanitiseFallback(raw: string): string {
  let msg = raw
    .replace(UUID_RE, '')                           // remove UUIDs
    .replace(/^(error|exception|db error):\s*/i, '') // remove prefixes
    .replace(/\s{2,}/g, ' ')                        // collapse whitespace
    .trim()

  // If what remains is very short, a raw code, or empty — use a generic message
  if (!msg || msg.length < 6 || /^[A-Z_]+$/.test(msg)) {
    return 'An unexpected error occurred. Please try again.'
  }

  return msg.charAt(0).toUpperCase() + msg.slice(1)
}

/**
 * Returns a human-friendly error message for the given backend code + raw message.
 * Prefer the code map; fall back to sanitising the raw message.
 */
export function friendlyMessage(code: string, rawMessage: string): string {
  const mapped = CODE_MAP[code]

  // Empty string in the map means "use the raw message" (e.g. VALIDATION_ERROR)
  if (mapped !== undefined) {
    return mapped || sanitiseFallback(rawMessage)
  }

  // Unknown code — sanitise whatever the backend sent
  return sanitiseFallback(rawMessage)
}
